import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Spin, Select, Input, DatePicker, Button, Card, Space, Divider, Row, Col, Empty } from 'antd';
import { FaEye, FaFilter, FaSearch, FaTimesCircle } from 'react-icons/fa';
import { apiHandle } from '../../config/apiHandle';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;

const OrderManagement = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Search state
  const [universalSearch, setUniversalSearch] = useState('');
  
  // Filter states
  const [orderStatus, setOrderStatus] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [fulfillmentStatus, setFulfillmentStatus] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [returnStatus, setReturnStatus] = useState(null);
  const [deliveryType, setDeliveryType] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [location, setLocation] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);
  const [noMatchesFound, setNoMatchesFound] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    applyFiltersAndSearch();
  }, [
    orders,
    orderStatus,
    paymentStatus,
    fulfillmentStatus,
    deliveryStatus,
    returnStatus,
    deliveryType,
    dateRange,
    location,
    productSearch,
    universalSearch
  ]);

  useEffect(() => {
    // Count active filters
    let count = 0;
    if (orderStatus) count++;
    if (paymentStatus) count++;
    if (fulfillmentStatus) count++;
    if (deliveryStatus) count++;
    if (returnStatus) count++;
    if (deliveryType) count++;
    if (dateRange) count++;
    if (location) count++;
    if (productSearch) count++;
    if (universalSearch) count++;
    
    setActiveFilters(count);
  }, [
    orderStatus,
    paymentStatus,
    fulfillmentStatus,
    deliveryStatus,
    returnStatus,
    deliveryType,
    dateRange,
    location,
    productSearch,
    universalSearch
  ]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await apiHandle.get('get-orders');
      setOrders(response.data?.data?.orders || []);
      setFilteredOrders(response.data?.data?.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search function to improve performance
  // const debouncedSearch = useCallback(
  //   debounce((searchValue) => {
  //     setUniversalSearch(searchValue);
  //     setSearchLoading(false);
  //   }, 50),
  //   []
  // );

  const handleSearchChange = (e) => {
    const searchValue = e.target.value;
    setSearchLoading(true);
    setUniversalSearch(searchValue);
    setSearchLoading(false);
    // debouncedSearch(searchValue);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setUniversalSearch(e.target.value);
      setSearchLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let result = [...orders];

    // First apply universal search if present
    if (universalSearch) {
      const searchTerm = universalSearch.toLowerCase().trim();
      
      result = result.filter(order => {
        // Order Number - exact match
        const orderIdMatch = order.order_id?.toLowerCase() === searchTerm;
        
        // Customer Name - partial match on first or last name
        const firstName = order.customer_info?.firstName?.toLowerCase() || '';
        const lastName = order.customer_info?.lastName?.toLowerCase() || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const nameMatch = 
          firstName.includes(searchTerm) || 
          lastName.includes(searchTerm) || 
          fullName.includes(searchTerm);
        
        // Customer Email - partial match
        const emailMatch = order.customer_info?.email?.toLowerCase()?.includes(searchTerm);
        
        // Customer Phone - exact match
        const phoneMatch = order.customer_info?.phone?.replace(/\D/g, '') === searchTerm.replace(/\D/g, '');
        
        return orderIdMatch || nameMatch || emailMatch || phoneMatch;
      });
    }

    // Then apply all other filters
    
    // Apply Order Status filter
    if (orderStatus) {
      result = result.filter(order => order.orderStatus === orderStatus);
    }

    // Apply Payment Status filter
    if (paymentStatus) {
      result = result.filter(order => order.paymentStatus === paymentStatus);
    }

    // Apply Fulfillment Status filter
    if (fulfillmentStatus) {
      if (fulfillmentStatus === 'Fulfilled') {
        result = result.filter(order => order.orderStatus === 'Fulfilled');
      } else if (fulfillmentStatus === 'Unfulfilled') {
        result = result.filter(order => order.orderStatus === 'Unfulfilled');
      } else if (fulfillmentStatus === 'Partially Fulfilled') {
        result = result.filter(order => order.orderStatus === 'Partially Fulfilled');
      }
    }

    // Apply delivery status filter
    if (deliveryStatus) {
      result = result.filter(order => {
        // Adjust based on your actual data structure
        return order.deliveryStatus === deliveryStatus;
      });
    }

    // Apply return status filter
    if (returnStatus) {
      result = result.filter(order => {
        // Adjust based on your actual data structure
        return order.returnStatus === returnStatus;
      });
    }

    // Apply delivery type filter
    if (deliveryType) {
      result = result.filter(order => {
        if (deliveryType === 'Shipping') {
          return order.order_type === 'normal' && !order.order_items.some(item => item.assign_store);
        } else if (deliveryType === 'Pickup in Store') {
          return order.order_items.some(item => item.assign_store);
        }
        return true;
      });
    }

    // Apply date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].startOf('day').toISOString();
      const endDate = dateRange[1].endOf('day').toISOString();
      
      result = result.filter(order => {
        const orderDate = new Date(order.createdAt).toISOString();
        return orderDate >= startDate && orderDate <= endDate;
      });
    }

    // Apply location filter
    if (location) {
      result = result.filter(order => {
        const customerCity = order.customer_info?.city?.toLowerCase() || '';
        const customerProvince = order.customer_info?.province?.toLowerCase() || '';
        const searchTerm = location.toLowerCase();
        
        return customerCity.includes(searchTerm) || customerProvince.includes(searchTerm);
      });
    }

    // Apply product search filter
    if (productSearch) {
      result = result.filter(order => {
        const searchTerm = productSearch.toLowerCase();
        
        // Search in product names
        return order.order_items.some(item => {
          const productName = item.product_id?.name?.toLowerCase() || '';
          const productSku = item.variant?.toLowerCase() || '';
          
          return productName.includes(searchTerm) || productSku.includes(searchTerm);
        });
      });
    }

    // Update state with filtered results
    setFilteredOrders(result);
    setNoMatchesFound(result.length === 0);
  };

  const resetFilters = () => {
    setOrderStatus(null);
    setPaymentStatus(null);
    setFulfillmentStatus(null);
    setDeliveryStatus(null);
    setReturnStatus(null);
    setDeliveryType(null);
    setDateRange(null);
    setLocation('');
    setProductSearch('');
    setUniversalSearch('');
  };

  const columns = [
    {
      title: 'Order #',
      dataIndex: 'order_id',
      key: 'order_id',
      sorter: (a, b) => a.order_id.localeCompare(b.order_id),
      render: (text, record) => {
        // Highlight the text if it matches search
        if (universalSearch && text?.toLowerCase() === universalSearch.toLowerCase()) {
          return <span className="bg-yellow-100">{text}</span>;
        }
        return text;
      }
    },
    {
      title: 'Customer Name',
      key: 'customer_name',
      render: (record) => {
        const firstName = record.customer_info?.firstName || '';
        const lastName = record.customer_info?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        // Highlight the name if it matches search
        if (universalSearch && 
            (firstName.toLowerCase().includes(universalSearch.toLowerCase()) || 
             lastName.toLowerCase().includes(universalSearch.toLowerCase()) ||
             fullName.toLowerCase().includes(universalSearch.toLowerCase()))) {
          return <span className="bg-yellow-100">{fullName}</span>;
        }
        return fullName;
      },
      sorter: (a, b) => {
        const nameA = `${a.customer_info?.firstName || ''} ${a.customer_info?.lastName || ''}`;
        const nameB = `${b.customer_info?.firstName || ''} ${b.customer_info?.lastName || ''}`;
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Email',
      dataIndex: ['customer_info', 'email'],
      key: 'email',
      render: (text) => {
        // Highlight the email if it matches search
        if (universalSearch && text?.toLowerCase().includes(universalSearch.toLowerCase())) {
          const startIndex = text.toLowerCase().indexOf(universalSearch.toLowerCase());
          const endIndex = startIndex + universalSearch.length;
          
          return (
            <>
              {text.substring(0, startIndex)}
              <span className="bg-yellow-100">{text.substring(startIndex, endIndex)}</span>
              {text.substring(endIndex)}
            </>
          );
        }
        return text;
      }
    },
    {
      title: 'Phone',
      dataIndex: ['customer_info', 'phone'],
      key: 'phone',
      render: (text) => {
        // Highlight the phone if it matches search (ignoring non-digits)
        if (universalSearch && text) {
          const cleanSearch = universalSearch.replace(/\D/g, '');
          const cleanPhone = text.replace(/\D/g, '');
          
          if (cleanPhone === cleanSearch) {
            return <span className="bg-yellow-100">{text}</span>;
          }
        }
        return text || 'N/A';
      }
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => `$${amount?.toFixed(2) || '0.00'}`,
      sorter: (a, b) => a.totalAmount - b.totalAmount,
    },
    {
      title: 'Order Status',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status}
        </Tag>
      ),
      filters: [
        { text: 'Fulfilled', value: 'Fulfilled' },
        { text: 'Unfulfilled', value: 'Unfulfilled' },
        { text: 'Canceled', value: 'Canceled' },
      ],
      onFilter: (value, record) => record.orderStatus === value,
    },
    {
      title: 'Payment Status',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status) => (
        <Tag color={status === 'Paid' ? 'green' : 'red'}>
          {status}
        </Tag>
      ),
      filters: [
        { text: 'Paid', value: 'Paid' },
        { text: 'Unpaid', value: 'Unpaid' },
      ],
      onFilter: (value, record) => record.paymentStatus === value,
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <div className="flex gap-2">
          <FaEye 
            onClick={() => navigate(`/order-management/${record?._id}`)} 
            size={20} 
            className="text-blue-500 cursor-pointer" 
            title="View Order Details"
          />
        </div>
      ),
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Fulfilled':
        return 'green';
      case 'Unfulfilled':
        return 'orange';
      case 'Partially Fulfilled':
        return 'blue';
      case 'Canceled':
        return 'red';
      default:
        return 'default';
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Order Management</h1>
        <Space>
          <Button 
            type="primary" 
            icon={<FaFilter />} 
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters {activeFilters > 0 && `(${activeFilters})`}
          </Button>
          {activeFilters > 0 && (
            <Button icon={<FaTimesCircle />} onClick={resetFilters}>
              Clear All
            </Button>
          )}
        </Space>
      </div>

      {/* Universal Search Bar */}
      <div className="mb-6">
        <Input
          placeholder="Search by order number, customer name, email, or phone..."
          prefix={<FaSearch className="text-gray-400" />}
          size="large"
          allowClear
          value={universalSearch}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          suffix={
            searchLoading ? <Spin size="small" /> : null
          }
          className="w-full"
        />
        <div className="text-gray-500 text-xs mt-1">
          Press Enter to search or type to filter as you go
        </div>
      </div>

      {showFilters && (
        <Card className="mb-6 bg-gray-50">
          <Divider orientation="left">Filter Options</Divider>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Order Status</label>
                <Select
                  placeholder="Select Order Status"
                  style={{ width: '100%' }}
                  value={orderStatus}
                  onChange={setOrderStatus}
                  allowClear
                >
                  <Option value="Fulfilled">Fulfilled</Option>
                  <Option value="Unfulfilled">Unfulfilled</Option>
                  <Option value="Canceled">Canceled</Option>
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Payment Status</label>
                <Select
                  placeholder="Select Payment Status"
                  style={{ width: '100%' }}
                  value={paymentStatus}
                  onChange={setPaymentStatus}
                  allowClear
                >
                  <Option value="Paid">Paid</Option>
                  <Option value="Unpaid">Unpaid</Option>
                  <Option value="Partially Paid">Partially Paid</Option>
                  <Option value="Partially Refund">Partially Refund</Option>
                  <Option value="Pending">Pending</Option>
                  <Option value="Refunded">Refunded</Option>
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Fulfillment Status</label>
                <Select
                  placeholder="Select Fulfillment Status"
                  style={{ width: '100%' }}
                  value={fulfillmentStatus}
                  onChange={setFulfillmentStatus}
                  allowClear
                >
                  <Option value="Fulfilled">Fulfilled</Option>
                  <Option value="Unfulfilled">Unfulfilled</Option>
                  <Option value="Partially Fulfilled">Partially Fulfilled</Option>
                  <Option value="Scheduled">Scheduled</Option>
                  <Option value="On Hold">On Hold</Option>
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Delivery Status</label>
                <Select
                  placeholder="Select Delivery Status"
                  style={{ width: '100%' }}
                  value={deliveryStatus}
                  onChange={setDeliveryStatus}
                  allowClear
                >
                  <Option value="In transit">In transit</Option>
                  <Option value="Out for delivery">Out for delivery</Option>
                  <Option value="Attempted delivery">Attempted delivery</Option>
                  <Option value="Delayed">Delayed</Option>
                  <Option value="Failed delivery">Failed delivery</Option>
                  <Option value="Delivered">Delivered</Option>
                  <Option value="Tracking added">Tracking added</Option>
                  <Option value="No status">No status</Option>
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Return Status</label>
                <Select
                  placeholder="Select Return Status"
                  style={{ width: '100%' }}
                  value={returnStatus}
                  onChange={setReturnStatus}
                  allowClear
                >
                  <Option value="Return in progress">Return in progress</Option>
                  <Option value="Return closed">Return closed</Option>
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Delivery Type</label>
                <Select
                  placeholder="Select Delivery Type"
                  style={{ width: '100%' }}
                  value={deliveryType}
                  onChange={setDeliveryType}
                  allowClear
                >
                  <Option value="Pickup in Store">Pickup in Store</Option>
                  <Option value="Shipping">Shipping</Option>
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Order Date Range</label>
                <RangePicker 
                  style={{ width: '100%' }} 
                  value={dateRange}
                  onChange={setDateRange}
                />
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Location</label>
                <Input
                  placeholder="City, Province, etc."
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  allowClear
                />
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Product Name or SKU</label>
                <Input
                  placeholder="Search by product or SKU"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  allowClear
                  prefix={<FaSearch className="text-gray-400" />}
                />
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {noMatchesFound ? (
        <Empty
          description={
            <div className="text-center">
              <p className="text-lg font-medium">No orders found matching your search.</p>
              <p className="text-gray-500">Try adjusting your search terms or filters.</p>
            </div>
          }
          className="py-12"
        />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredOrders}
          loading={loading}
          rowKey="_id"
          pagination={{ 
            pageSize: 10,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`
          }}
          scroll={{ x: true }}
          summary={(pageData) => {
            let totalValue = 0;
            pageData.forEach(({ totalAmount }) => {
              totalValue += totalAmount || 0;
            });

            return (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    <strong>Total Orders: {filteredOrders.length}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>
                    <strong>${totalValue.toFixed(2)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} colSpan={3}></Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            );
          }}
        />
      )}
    </div>
  );
};

export default OrderManagement;